/**
 * Editorial commerce storefront theme scripts
 * Complete theme JavaScript
 */
(function () {
  'use strict';

  /* ======================================================================
     UTILITY FUNCTIONS
     ====================================================================== */

  /**
   * Format cents into a money string using the shop's money format.
   * Supports {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
   * {{amount_no_decimals_with_comma_separator}}, {{amount_with_space_separator}},
   * {{amount_no_decimals_with_space_separator}}, and {{amount_with_apostrophe_separator}}.
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    var fmt = format || window.theme.moneyFormat || '${{amount}}';

    function addCommas(n) {
      return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }

    function addSpaces(n) {
      return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
    }

    function addApostrophes(n) {
      return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1'");
    }

    var value = '';
    var patt = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(patt);

    if (!match) return fmt;

    switch (match[1]) {
      case 'amount':
        value = addCommas((cents / 100).toFixed(2));
        break;
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100).toString());
        break;
      case 'amount_with_comma_separator':
        value = (cents / 100).toFixed(2).replace('.', ',');
        value = value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = Math.round(cents / 100).toString();
        value = value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
        break;
      case 'amount_with_space_separator':
        value = (cents / 100).toFixed(2).replace('.', ',');
        value = addSpaces(value);
        break;
      case 'amount_no_decimals_with_space_separator':
        value = addSpaces(Math.round(cents / 100).toString());
        break;
      case 'amount_with_apostrophe_separator':
        value = (cents / 100).toFixed(2);
        value = addApostrophes(value);
        break;
      default:
        value = addCommas((cents / 100).toFixed(2));
    }

    return fmt.replace(patt, value);
  }

  /**
   * Debounce: delay function execution until after a wait period.
   */
  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  /**
   * Return a fetch configuration object for Shopify AJAX API calls.
   */
  function fetchConfig(type) {
    type = type || 'json';
    var config = {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    };
    if (type === 'json') {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  }

  /**
   * Serialize form data into a plain object.
   */
  function serializeForm(form) {
    var obj = {};
    var formData = new FormData(form);
    formData.forEach(function (value, key) {
      if (obj[key] !== undefined) {
        if (!Array.isArray(obj[key])) {
          obj[key] = [obj[key]];
        }
        obj[key].push(value);
      } else {
        obj[key] = value;
      }
    });
    return obj;
  }

  function initializeResultsComparisons(root) {
    var scope = root || document;

    scope.querySelectorAll('[data-results-compare] input[type="range"]').forEach(function (input) {
      if (input.dataset.resultsReady === 'true') return;
      input.dataset.resultsReady = 'true';

      function updateComparison() {
        input.closest('[data-results-compare]').style.setProperty('--split-position', input.value + '%');
      }

      input.addEventListener('input', updateComparison);
      input.addEventListener('keydown', function (event) {
        var nextValue = parseInt(input.value, 10);
        var minimum = parseInt(input.min, 10);
        var maximum = parseInt(input.max, 10);

        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') nextValue -= 1;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') nextValue += 1;
        else if (event.key === 'Home') nextValue = minimum;
        else if (event.key === 'End') nextValue = maximum;
        else return;

        event.preventDefault();
        input.value = Math.max(minimum, Math.min(maximum, nextValue));
        updateComparison();
      });
    });

    scope.querySelectorAll('[data-results-carousel]').forEach(function (carousel) {
      if (carousel.dataset.resultsReady === 'true') return;
      carousel.dataset.resultsReady = 'true';

      var track = carousel.querySelector('[data-results-track]');
      var previous = carousel.querySelector('[data-results-previous]');
      var next = carousel.querySelector('[data-results-next]');
      var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-results-dot]'));
      if (!track || !previous || !next) return;

      function move(direction) {
        var card = track.querySelector('.pdp-flow__result-card');
        var gap = parseFloat(window.getComputedStyle(track).gap) || 0;
        var distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
        track.scrollBy({
          left: direction * distance,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }

      previous.addEventListener('click', function () { move(-1); });
      next.addEventListener('click', function () { move(1); });

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var card = track.querySelector('.pdp-flow__result-card');
          var gap = parseFloat(window.getComputedStyle(track).gap) || 0;
          var index = parseInt(dot.dataset.resultsDot, 10) || 0;
          if (!card) return;
          track.scrollTo({
            left: index * (card.getBoundingClientRect().width + gap),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        });
      });

      track.addEventListener('scroll', debounce(function () {
        var card = track.querySelector('.pdp-flow__result-card');
        var gap = parseFloat(window.getComputedStyle(track).gap) || 0;
        if (!card || !dots.length) return;
        var index = Math.round(track.scrollLeft / (card.getBoundingClientRect().width + gap));
        dots.forEach(function (dot, dotIndex) {
          var active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }, 80));
    });
  }

  /**
   * Lock body scrolling.
   */
  function lockScroll() {
    document.body.classList.add('scroll-locked');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  /**
   * Unlock body scrolling.
   */
  function unlockScroll() {
    var cartOpen = document.getElementById('CartDrawer') && document.getElementById('CartDrawer').classList.contains('is-open');
    var menuOpen = document.getElementById('MobileMenu') && document.getElementById('MobileMenu').classList.contains('is-open');
    var searchOpen = document.getElementById('SearchOverlay') && document.getElementById('SearchOverlay').classList.contains('is-open');
    var accountOpen = document.getElementById('AccountDrawer') && document.getElementById('AccountDrawer').classList.contains('is-open');
    var supportOpen = document.getElementById('SupportDrawer') && document.getElementById('SupportDrawer').classList.contains('is-open');

    if (!cartOpen && !menuOpen && !searchOpen && !accountOpen && !supportOpen) {
      document.body.classList.remove('scroll-locked');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }

  /* ======================================================================
     CART DRAWER
     ====================================================================== */
  var CartDrawer = (function () {
    var drawer = document.getElementById('CartDrawer');
    var body = document.getElementById('CartDrawerBody');
    var emptyState = document.getElementById('CartDrawerEmpty');
    var itemsContainer = document.getElementById('CartDrawerItems');
    var footer = document.getElementById('CartDrawerFooter');
    var subtotalEl = document.getElementById('CartDrawerSubtotal');
    var progressEl = drawer ? drawer.querySelector('[data-cart-progress]') : null;
    var progressMessage = drawer ? drawer.querySelector('[data-cart-progress-message]') : null;
    var progressBar = drawer ? drawer.querySelector('[data-cart-progress-bar]') : null;
    var upsellsEl = drawer ? drawer.querySelector('[data-cart-upsells]') : null;
    var drawerMessage = drawer ? drawer.querySelector('[data-cart-drawer-message]') : null;
    var noteInput = drawer ? drawer.querySelector('[data-cart-drawer-note]') : null;
    var noteMessage = drawer ? drawer.querySelector('[data-cart-drawer-note-message]') : null;

    if (!drawer) return { open: function () {}, close: function () {}, refresh: function () {} };

    function open(skipRefresh) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll();
      if (!skipRefresh) {
        return refresh();
      }
      return Promise.resolve();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }

    function refresh(options) {
      if (!options || !options.keepMessage) {
        setDrawerMessage('', '');
      }
      return fetch(window.theme.routes.cart_url + '.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderCart(cart);
          return cart;
        })
        .catch(function (err) {
          console.error('Cart fetch error:', err);
          setDrawerMessage('Could not load your cart. Please refresh and try again.', 'error');
          return null;
        });
    }

    function setDrawerMessage(message, tone) {
      if (!drawerMessage) return;
      drawerMessage.textContent = message || '';
      drawerMessage.hidden = !message;
      drawerMessage.classList.toggle('cart-drawer__message--error', tone === 'error');
      drawerMessage.classList.toggle('cart-drawer__message--success', tone === 'success');
    }

    function getErrorMessage(err, fallback) {
      return (err && (err.description || err.message)) ? (err.description || err.message) : fallback;
    }

    function setNoteMessage(message, tone) {
      if (!noteMessage) return;
      noteMessage.textContent = message || '';
      noteMessage.hidden = !message;
      noteMessage.classList.toggle('cart-drawer__notes-message--error', tone === 'error');
      noteMessage.classList.toggle('cart-drawer__notes-message--success', tone === 'success');
    }

    var updateNote = debounce(function (note) {
      if (!window.theme.routes.cart_update_url) return;
      var config = fetchConfig('json');
      config.body = JSON.stringify({ note: note });

      fetch(window.theme.routes.cart_update_url + '.js', config)
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (data) { throw data; });
          }
          return r.json();
        })
        .then(function () {
          setNoteMessage('Order note saved.', 'success');
        })
        .catch(function (err) {
          console.error('Cart note update error:', err);
          setNoteMessage(getErrorMessage(err, 'Could not save your note. Please try again.'), 'error');
        });
    }, 600);

    function renderCart(cart) {
      updateCartCount(cart.item_count);
      updateProgress(cart);
      updateUpsells(cart);

      if (cart.item_count === 0) {
        emptyState.style.display = '';
        itemsContainer.style.display = 'none';
        footer.style.display = 'none';
        itemsContainer.innerHTML = '';
        return;
      }

      emptyState.style.display = 'none';
      itemsContainer.style.display = '';
      footer.style.display = '';
      subtotalEl.textContent = formatMoney(cart.total_price);
      if (noteInput && document.activeElement !== noteInput) {
        noteInput.value = cart.note || '';
      }

      var html = '';
      cart.items.forEach(function (item) {
        var imgSrc = '';
        if (item.featured_image) {
          if (typeof item.featured_image === 'string') {
            imgSrc = item.featured_image.replace(/(\.[^.]*)$/, '_150x$1');
          } else if (item.featured_image.url) {
            imgSrc = item.featured_image.url + '&width=150';
          }
        }
        if (!imgSrc && item.image) {
          imgSrc = typeof item.image === 'string' ? item.image.replace(/(\.[^.]*)$/, '_150x$1') : '';
        }

        var variantTitle = item.variant_title || '';
        var reviewNote = getCartItemReviewNote(item);

        html += '<div class="cart-drawer__item" data-cart-drawer-item data-line-key="' + item.key + '">';
        html += '  <div class="cart-drawer__item-image">';
        if (imgSrc) {
          html += '    <img src="' + imgSrc + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="75" height="75">';
        }
        html += '  </div>';
        html += '  <div class="cart-drawer__item-details">';
        html += '    <a href="' + item.url + '" class="cart-drawer__item-title">' + escapeHtml(item.product_title) + '</a>';
        if (variantTitle) {
          html += '    <p class="cart-drawer__item-variant">' + escapeHtml(variantTitle) + '</p>';
        }
        if (reviewNote) {
          html += '    <p class="cart-drawer__item-signal">' + escapeHtml(reviewNote) + '</p>';
        }
        html += '    <p class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</p>';
        html += '    <div class="cart-drawer__item-quantity">';
        html += '      <button class="cart-drawer__qty-btn" data-cart-drawer-minus data-key="' + item.key + '" aria-label="Decrease quantity">&#8722;</button>';
        html += '      <input class="cart-drawer__qty-input" type="number" value="' + item.quantity + '" min="0" data-cart-drawer-qty data-key="' + item.key + '" aria-label="Quantity">';
        html += '      <button class="cart-drawer__qty-btn" data-cart-drawer-plus data-key="' + item.key + '" aria-label="Increase quantity">&#43;</button>';
        html += '    </div>';
        html += '  </div>';
        html += '  <button class="cart-drawer__item-remove" data-cart-drawer-remove data-key="' + item.key + '" aria-label="Remove item">&times;</button>';
        html += '</div>';
      });

      itemsContainer.innerHTML = html;
    }

    function updateProgress(cart) {
      if (!progressEl || !progressMessage || !progressBar) return;
      var threshold = parseInt(progressEl.getAttribute('data-threshold'), 10) || 15000;
      var total = cart.total_price || 0;
      var remaining = Math.max(threshold - total, 0);
      var percent = Math.min(Math.round((total / threshold) * 100), 100);
      var isUnlocked = remaining === 0;
      progressEl.classList.toggle('cart-drawer__progress--unlocked', isUnlocked);
      progressBar.style.width = percent + '%';
      progressMessage.textContent = isUnlocked
        ? 'FREE SHIPPING UNLOCKED'
        : 'You are ' + formatMoney(remaining) + ' away from free shipping.';
    }

    function updateUpsells(cart) {
      if (!upsellsEl) return;
      var productIds = (cart.items || []).map(function (item) { return String(item.product_id); });
      var groups = upsellsEl.querySelectorAll('[data-cart-upsell-group]');

      function showGroup(group) {
        if (!group) return 0;
        var visibleCount = 0;
        group.querySelectorAll('[data-cart-upsell-item]').forEach(function (itemEl) {
          var isInCart = productIds.indexOf(itemEl.getAttribute('data-product-id')) !== -1;
          var limit = parseInt(itemEl.getAttribute('data-visible-limit'), 10) || 3;
          var shouldShow = !isInCart && visibleCount < limit;
          itemEl.hidden = !shouldShow;
          if (shouldShow) visibleCount += 1;
        });
        group.hidden = visibleCount === 0;
        return visibleCount;
      }

      if (!groups.length) {
        var visibleCount = showGroup(upsellsEl);
        upsellsEl.hidden = visibleCount === 0;
        return;
      }

      groups.forEach(function (group) {
        group.hidden = true;
      });

      var mode = getCartUpsellMode(cart);
      var activeGroup = upsellsEl.querySelector('[data-cart-upsell-group="' + mode + '"]');
      var defaultGroup = upsellsEl.querySelector('[data-cart-upsell-group="default"]');
      var activeCount = showGroup(activeGroup || defaultGroup);

      if (activeCount === 0 && defaultGroup && defaultGroup !== activeGroup) {
        activeCount = showGroup(defaultGroup);
      }

      upsellsEl.hidden = activeCount === 0;
    }

    function getCartUpsellMode(cart) {
      return 'default';
    }

    function getCartItemReviewNote(item) {
      var signal = [
        item.product_type,
        item.product_title,
        item.title,
        item.handle,
        item.url,
        item.sku
      ].join(' ').toLowerCase();

      if (/preorder|pre-order|coming-soon/.test(signal)) {
        return 'Preorder timing may differ from standard dispatch.';
      }

      if (/final-sale|final sale|last-chance/.test(signal)) {
        return 'Return restrictions may apply unless faulty.';
      }

      return '';
    }

    function updateItem(key, quantity) {
      var config = fetchConfig('json');
      config.body = JSON.stringify({ id: key, quantity: quantity });
      setDrawerMessage('', '');

      return fetch(window.theme.routes.cart_change_url + '.js', config)
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (data) { throw data; });
          }
          return r.json();
        })
        .then(function (cart) {
          renderCart(cart);
          return cart;
        })
        .catch(function (err) {
          console.error('Cart update error:', err);
          refresh({ keepMessage: true }).finally(function () {
            setDrawerMessage(getErrorMessage(err, 'Could not update your cart. Please try again.'), 'error');
          });
        });
    }

    function removeItem(key) {
      updateItem(key, 0);
    }

    function addItem(formData) {
      var config = fetchConfig('json');
      config.body = JSON.stringify(formData);

      return fetch(window.theme.routes.cart_add_url + '.js', config)
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (data) { throw data; });
          }
          return r.json();
        })
        .then(function (item) {
          if (window.theme.cartDrawerEnabled) {
            return refresh().then(function () {
              open(true);
              return item;
            });
          }
          refresh();
          return item;
        });
    }

    function updateCartCount(count) {
      var badges = document.querySelectorAll('.cart-count');
      badges.forEach(function (badge) {
        badge.textContent = count;
        if (count > 0) {
          badge.classList.add('cart-count--has-items');
        } else {
          badge.classList.remove('cart-count--has-items');
        }
      });
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }

    // Event delegation for cart drawer interactions
    drawer.addEventListener('click', function (e) {
      // Close buttons & overlay
      if (e.target.closest('[data-cart-drawer-close]')) {
        e.preventDefault();
        close();
        return;
      }

      // Minus button
      var minusBtn = e.target.closest('[data-cart-drawer-minus]');
      if (minusBtn) {
        e.preventDefault();
        var key = minusBtn.getAttribute('data-key');
        var input = itemsContainer.querySelector('[data-cart-drawer-qty][data-key="' + key + '"]');
        var qty = parseInt(input.value, 10) - 1;
        if (qty < 0) qty = 0;
        input.value = qty;
        updateItem(key, qty);
        return;
      }

      // Plus button
      var plusBtn = e.target.closest('[data-cart-drawer-plus]');
      if (plusBtn) {
        e.preventDefault();
        var pKey = plusBtn.getAttribute('data-key');
        var pInput = itemsContainer.querySelector('[data-cart-drawer-qty][data-key="' + pKey + '"]');
        var pQty = parseInt(pInput.value, 10) + 1;
        pInput.value = pQty;
        updateItem(pKey, pQty);
        return;
      }

      // Remove button
      var removeBtn = e.target.closest('[data-cart-drawer-remove]');
      if (removeBtn) {
        e.preventDefault();
        removeItem(removeBtn.getAttribute('data-key'));
        return;
      }

      var upsellBtn = e.target.closest('[data-cart-upsell-add]');
      if (upsellBtn) {
        e.preventDefault();
        var variantId = parseInt(upsellBtn.getAttribute('data-variant-id'), 10);
        if (!variantId) return;
        var upsellGroup = upsellBtn.closest('[data-cart-upsell-group]');
        var upsellDiscountCode = upsellGroup ? upsellGroup.getAttribute('data-bundle-discount-code') : '';
        upsellBtn.disabled = true;
        upsellBtn.classList.add('is-loading');
        addItem({ items: [{ id: variantId, quantity: 1 }] })
          .then(function () {
            if (upsellDiscountCode && window.themeApplyDiscountCode) {
              window.themeApplyDiscountCode(upsellDiscountCode);
              setDrawerMessage('Added to cart. ' + upsellDiscountCode + ' will apply at checkout.', 'success');
            } else {
              setDrawerMessage('Added to cart.', 'success');
            }
          })
          .catch(function (err) {
            console.error('Cart upsell add error:', err);
            setDrawerMessage(getErrorMessage(err, 'Could not add this item. Please check availability.'), 'error');
          })
          .finally(function () {
            upsellBtn.disabled = false;
            upsellBtn.classList.remove('is-loading');
          });
        return;
      }
    });

    // Direct quantity input changes (debounced)
    drawer.addEventListener('change', debounce(function (e) {
      if (e.target.matches('[data-cart-drawer-qty]')) {
        var val = parseInt(e.target.value, 10);
        if (isNaN(val) || val < 0) val = 0;
        updateItem(e.target.getAttribute('data-key'), val);
      }
    }, 300));

    if (noteInput) {
      noteInput.addEventListener('input', function () {
        setNoteMessage('Saving note...', '');
        updateNote(noteInput.value);
      });
    }

    return {
      open: open,
      close: close,
      refresh: refresh,
      addItem: addItem
    };
  })();

  window.theme = window.theme || {};
  window.theme.cart = CartDrawer;

  /* ======================================================================
     PRODUCT CARD QUICK ADD
     ====================================================================== */
  (function () {
    function closePanels(exceptCard, restoreFocus) {
      document.querySelectorAll('[data-product-card]').forEach(function (card) {
        if (exceptCard && card === exceptCard) return;
        var panel = card.querySelector('[data-quick-add-panel]');
        var trigger = card.querySelector('[data-quick-add]');
        if (panel) panel.hidden = true;
        card.classList.remove('is-quick-add-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus && trigger) trigger.focus();
      });
    }

    function getVariantLabel(button) {
      if (!button) return '';
      var labelEl = button.querySelector('span');
      var label = labelEl ? labelEl.textContent : button.textContent;
      label = (label || '').replace(/\s+/g, ' ').trim();
      if (!label || /^add( to (bag|cart))?$/i.test(label) || /^default title$/i.test(label)) return '';
      return label;
    }

    function setButtonLoading(button, loading) {
      if (!button) return;
      button.disabled = loading;
      button.classList.toggle('is-loading', loading);
    }

    function setCardMessage(card, message, tone) {
      if (!card) return;
      var messageEl = card.querySelector('[data-quick-add-message]');
      if (!messageEl) return;
      messageEl.textContent = message || '';
      messageEl.hidden = !message;
      messageEl.classList.toggle('product-card__quick-message--error', tone === 'error');
      messageEl.classList.toggle('product-card__quick-message--success', tone === 'success');
    }

    function getBundleDiscount(button, card) {
      var source = button || card;
      var code = source ? source.getAttribute('data-bundle-discount-code') : '';
      var percent = source ? source.getAttribute('data-bundle-discount-percent') : '';
      var label = source ? source.getAttribute('data-bundle-offer-label') : '';

      if ((!code || !percent || !label) && card) {
        code = code || card.getAttribute('data-bundle-discount-code');
        percent = percent || card.getAttribute('data-bundle-discount-percent');
        label = label || card.getAttribute('data-bundle-offer-label');
      }

      code = String(code || '').replace(/\s+/g, '').toUpperCase();
      percent = parseInt(percent, 10) || 0;
      return code && percent > 0 ? { code: code, percent: percent, label: label || 'Bundle and save' } : null;
    }

    function addVariant(button, variantId) {
      var id = parseInt(variantId, 10);
      if (!id) return;
      var card = button.closest('[data-product-card]');
      var originalHtml = button.innerHTML;
      var variantLabel = getVariantLabel(button);
      var bundleDiscount = getBundleDiscount(button, card);
      var lineItem = { id: id, quantity: 1 };

      if (bundleDiscount) {
        lineItem.properties = {
          _bundle_discount_code: bundleDiscount.code,
          _bundle_discount_label: bundleDiscount.label,
          _bundle_discount_percent: String(bundleDiscount.percent)
        };
      }

      setCardMessage(card, '', '');
      setButtonLoading(button, true);
      button.setAttribute('aria-busy', 'true');
      CartDrawer.addItem({ items: [lineItem] })
        .then(function () {
          if (bundleDiscount && window.themeApplyDiscountCode) {
            window.themeApplyDiscountCode(bundleDiscount.code);
          }
          setCardMessage(
            card,
            bundleDiscount
              ? 'Added to cart. ' + bundleDiscount.code + ' will apply at checkout.'
              : (variantLabel ? variantLabel + ' added to cart.' : 'Added to cart.'),
            'success'
          );
          if (card) closePanels(card);
          closePanels();
        })
        .catch(function (err) {
          var message = (err && err.description) ? err.description : 'Could not add item to cart.';
          setCardMessage(card, message, 'error');
        })
        .finally(function () {
          setButtonLoading(button, false);
          button.removeAttribute('aria-busy');
          button.innerHTML = originalHtml;
        });
    }

    function handleTrigger(trigger, e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      var card = trigger.closest('[data-product-card]');
      var panel = card ? card.querySelector('[data-quick-add-panel]') : null;
      var variantsCount = parseInt(trigger.getAttribute('data-variants-count'), 10) || 1;

      if (panel && variantsCount > 1) {
        var willOpen = panel.hidden;
        closePanels(card);
        panel.hidden = !willOpen;
        card.classList.toggle('is-quick-add-open', willOpen);
        trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) {
          setCardMessage(card, '', '');
          window.setTimeout(function () {
            var firstAvailableOption = panel.querySelector('[data-card-variant-add]:not([disabled])');
            if (firstAvailableOption) firstAvailableOption.focus();
          }, 0);
        }
        return false;
      }

      addVariant(trigger, trigger.getAttribute('data-variant-id'));
      return false;
    }

    function handleVariantButton(button, e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      addVariant(button, button.getAttribute('data-variant-id'));
      return false;
    }

    window.themeQuickAddToggle = handleTrigger;
    window.themeQuickAddVariant = handleVariantButton;

    function closestFromEvent(e, selector) {
      var target = e.target;
      if (!target) return null;
      if (target.closest) return target.closest(selector);
      if (target.parentElement && target.parentElement.closest) return target.parentElement.closest(selector);
      return null;
    }

    document.addEventListener('click', function (e) {
      var trigger = closestFromEvent(e, '[data-quick-add]');
      if (trigger) {
        handleTrigger(trigger, e);
        return;
      }

      var variantButton = closestFromEvent(e, '[data-card-variant-add]');
      if (variantButton) {
        handleVariantButton(variantButton, e);
        return;
      }

      if (!closestFromEvent(e, '[data-product-card]')) {
        closePanels();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closePanels(null, true);
      }
    });
  })();

  /* ======================================================================
     MOBILE MENU
     ====================================================================== */
  var MobileMenu = (function () {
    var menu = document.getElementById('MobileMenu');
    if (!menu) return { open: function () {}, close: function () {} };

    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      lockScroll();
    }

    function close() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      menu.querySelectorAll('.mobile-menu__item.is-open').forEach(function (item) {
        item.classList.remove('is-open');
        var button = item.querySelector('[data-mobile-submenu-toggle]');
        var submenu = item.querySelector('.mobile-menu__submenu');
        if (button) button.setAttribute('aria-expanded', 'false');
        if (submenu) submenu.style.maxHeight = null;
      });
      unlockScroll();
    }

    function toggleSubmenu(button) {
      var parent = button.closest('.mobile-menu__item');
      if (!parent) return;

      var submenu = parent.querySelector('.mobile-menu__submenu');
      if (!submenu) return;

      var isOpen = parent.classList.contains('is-open');

      // Close other open submenus
      var allOpen = menu.querySelectorAll('.mobile-menu__item.is-open');
      allOpen.forEach(function (item) {
        if (item !== parent) {
          item.classList.remove('is-open');
          var toggle = item.querySelector('[data-mobile-submenu-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          var sub = item.querySelector('.mobile-menu__submenu');
          if (sub) sub.style.maxHeight = null;
        }
      });

      if (isOpen) {
        parent.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        submenu.style.maxHeight = null;
      } else {
        parent.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        submenu.style.maxHeight = submenu.scrollHeight + 'px';
      }
    }

    // Event delegation
    menu.addEventListener('click', function (e) {
      if (e.target.closest('[data-mobile-menu-close]')) {
        e.preventDefault();
        close();
        return;
      }

      var submenuToggle = e.target.closest('[data-mobile-submenu-toggle]');
      if (submenuToggle) {
        e.preventDefault();
        toggleSubmenu(submenuToggle);
        return;
      }
    });

    return {
      open: open,
      close: close
    };
  })();

  /* ======================================================================
     SEARCH OVERLAY
     ====================================================================== */
  var SearchOverlay = (function () {
    var overlay = document.getElementById('SearchOverlay');
    if (!overlay) return { open: function () {}, close: function () {} };

    var input = overlay.querySelector('.search-overlay__input');
    var defaults = overlay.querySelector('[data-search-defaults]');
    var results = overlay.querySelector('[data-predictive-search]');
    var statusEl = overlay.querySelector('[data-predictive-search-status]');
    var productsEl = overlay.querySelector('[data-predictive-search-products]');
    var linksEl = overlay.querySelector('[data-predictive-search-links]');
    var fallbackEl = overlay.querySelector('[data-search-fallback]');
    var searchTimer;
    var activeController;

    function escapeHtml(value) {
      return String(value || '').replace(/[&<>"']/g, function (char) {
        return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char];
      });
    }

    function setPanelState(state, message) {
      if (statusEl) statusEl.textContent = message || '';
      if (defaults) defaults.hidden = state !== 'default';
      if (results) results.hidden = state === 'default';
      if (fallbackEl && state === 'default') fallbackEl.hidden = true;
    }

    function imageUrlFromResult(item) {
      var image = item && (item.image || item.featured_image || item.featuredImage);
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.url || image.src || '';
    }

    function priceFromResult(item) {
      if (!item) return '';
      if (typeof item.price === 'string') {
        return /^[0-9]+(\.[0-9]+)?$/.test(item.price) ? formatMoney(Math.round(parseFloat(item.price) * 100)) : item.price;
      }
      if (typeof item.price_min === 'string') {
        return /^[0-9]+(\.[0-9]+)?$/.test(item.price_min) ? formatMoney(Math.round(parseFloat(item.price_min) * 100)) : item.price_min;
      }
      if (typeof item.price === 'number') return formatMoney(item.price);
      if (typeof item.price_min === 'number') return formatMoney(item.price_min);
      return '';
    }

    function renderProduct(item) {
      var title = escapeHtml(item.title);
      var url = escapeHtml(item.url || (window.theme.routes.search_url + '?q=' + encodeURIComponent(item.title || '')));
      var price = escapeHtml(priceFromResult(item));
      var imageUrl = imageUrlFromResult(item);
      var media = imageUrl
        ? '<img src="' + escapeHtml(imageUrl) + '" alt="' + title + '" loading="lazy">'
        : '<span>' + escapeHtml((item.title || 'C').charAt(0)) + '</span>';

      return '<a class="search-overlay__product" href="' + url + '">' +
        '<span class="search-overlay__product-media">' + media + '</span>' +
        '<span class="search-overlay__product-info">' +
        '<span class="search-overlay__product-title">' + title + '</span>' +
        (price ? '<span class="search-overlay__product-price">' + price + '</span>' : '') +
        '</span>' +
        '</a>';
    }

    function renderLink(item, type) {
      var title = escapeHtml(item.title);
      var url = escapeHtml(item.url || (window.theme.routes.search_url + '?q=' + encodeURIComponent(item.title || '')));
      return '<a class="search-overlay__suggestion" href="' + url + '">' +
        '<span>' + title + '</span>' +
        '<small>' + escapeHtml(type) + '</small>' +
        '</a>';
    }

    function renderPredictiveResults(payload, query) {
      var data = payload && payload.resources && payload.resources.results ? payload.resources.results : {};
      var products = data.products || [];
      var collections = data.collections || [];
      var pages = data.pages || [];
      var linkHtml = collections.map(function (item) { return renderLink(item, 'Collection'); })
        .concat(pages.map(function (item) { return renderLink(item, 'Page'); }));

      if (productsEl) {
        productsEl.innerHTML = products.length
          ? '<p class="search-overlay__eyebrow">Products</p>' + products.slice(0, 6).map(renderProduct).join('')
          : '';
      }

      if (linksEl) {
        linksEl.innerHTML = linkHtml.length
          ? '<p class="search-overlay__eyebrow">More matches</p>' + linkHtml.slice(0, 6).join('')
          : '';
      }

      if (!products.length && !linkHtml.length) {
        if (fallbackEl) fallbackEl.hidden = false;
        setPanelState('results', 'No exact matches for "' + query + '". Try a popular search below.');
      } else {
        if (fallbackEl) fallbackEl.hidden = true;
        setPanelState('results', 'Showing suggestions for "' + query + '".');
      }
    }

    function fetchPredictiveResults(query) {
      if (!query || query.length < 2) {
        if (productsEl) productsEl.innerHTML = '';
        if (linksEl) linksEl.innerHTML = '';
        setPanelState('default', '');
        return;
      }

      if (activeController) activeController.abort();
      activeController = window.AbortController ? new AbortController() : null;

      setPanelState('results', 'Searching "' + query + '"...');
      if (fallbackEl) fallbackEl.hidden = true;

      var endpoint = window.theme.routes.search_url +
        '/suggest.json?q=' + encodeURIComponent(query) +
        '&resources[type]=product,collection,page' +
        '&resources[limit]=6' +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=hide' +
        '&resources[options][fields]=title,product_type,variants.title,tag';

      fetch(endpoint, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
        signal: activeController ? activeController.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search unavailable');
          return response.json();
        })
        .then(function (payload) {
          renderPredictiveResults(payload, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (productsEl) productsEl.innerHTML = '';
          if (linksEl) linksEl.innerHTML = '';
          if (fallbackEl) fallbackEl.hidden = false;
          setPanelState('results', 'Search suggestions are not available right now. Press enter to search the full store.');
        });
    }

    function handleInput() {
      var query = input ? input.value.trim() : '';
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        fetchPredictiveResults(query);
      }, 180);
    }

    function open() {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      lockScroll();
      if (input && !input.value.trim()) {
        setPanelState('default', '');
      } else if (input) {
        fetchPredictiveResults(input.value.trim());
      }
      if (input) {
        setTimeout(function () { input.focus(); }, 100);
      }
    }

    function close() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }

    if (input) {
      input.addEventListener('input', handleInput);
    }

    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-close]')) {
        e.preventDefault();
        close();
        return;
      }

      // Close when clicking the outer overlay area (not inner form area)
      if (e.target === overlay) {
        close();
      }
    });

    return {
      open: open,
      close: close
    };
  })();

  /* ======================================================================
     ACCOUNT DRAWER
     ====================================================================== */
  var AccountDrawer = (function () {
    var drawer = document.getElementById('AccountDrawer');
    if (!drawer) return { open: function () {}, close: function () {} };

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-account-drawer-close]')) {
        close();
      }
    });

    return {
      open: open,
      close: close
    };
  })();

  /* ======================================================================
     SUPPORT DRAWER
     ====================================================================== */
  var SupportDrawer = (function () {
    var drawer = document.getElementById('SupportDrawer');
    var toggles = document.querySelectorAll('[data-support-drawer-toggle]');
    if (!drawer) return { open: function () {}, close: function () {} };

    function setExpanded(value) {
      toggles.forEach(function (toggle) {
        toggle.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      setExpanded(true);
      lockScroll();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      setExpanded(false);
      unlockScroll();
    }

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-support-drawer-close]')) {
        close();
      }
    });

    return {
      open: open,
      close: close
    };
  })();

  /* ======================================================================
     GLOBAL EVENT DELEGATION
     ====================================================================== */
  document.addEventListener('click', function (e) {
    // Cart drawer open (any element with data-cart-drawer-toggle or href to cart that should open drawer)
    if (e.target.closest('[data-cart-drawer-toggle]')) {
      e.preventDefault();
      CartDrawer.open();
      return;
    }

    // Mobile menu toggle
    if (e.target.closest('[data-mobile-menu-toggle]')) {
      e.preventDefault();
      MobileMenu.open();
      return;
    }

    // Search toggle
    if (e.target.closest('[data-search-toggle]')) {
      e.preventDefault();
      SearchOverlay.open();
      return;
    }

    if (e.target.closest('[data-account-drawer-toggle]')) {
      e.preventDefault();
      AccountDrawer.open();
      return;
    }

    if (e.target.closest('[data-support-drawer-toggle]')) {
      e.preventDefault();
      SupportDrawer.open();
      return;
    }
  });

  /* ======================================================================
     DISCOUNT CODE APPLY
     ====================================================================== */
  (function () {
    var preferredDiscountStorageKey = 'themePreferredDiscountCode';

    function normalizeDiscountCode(code) {
      return String(code || '').replace(/\s+/g, '').toUpperCase();
    }

    function getDiscountUrl(code, redirect) {
      return '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirect || '/checkout');
    }

    function storePreferredDiscountCode(code) {
      try {
        if (code) {
          window.localStorage.setItem(preferredDiscountStorageKey, code);
        }
      } catch (err) {
        // localStorage can be unavailable in private browsing; checkout forms still sync in-page.
      }
    }

    function getPreferredDiscountCode() {
      try {
        return normalizeDiscountCode(window.localStorage.getItem(preferredDiscountStorageKey));
      } catch (err) {
        return '';
      }
    }

    function setDiscountMessage(form, message) {
      var messageEl = form ? form.querySelector('[data-discount-message]') : null;
      if (!messageEl) return;
      messageEl.textContent = message;
      messageEl.hidden = !message;
    }

    function getCheckoutForm(form) {
      if (!form) return null;
      var formId = form.getAttribute('data-discount-checkout-form');
      return formId ? document.getElementById(formId) : null;
    }

    function setDiscountFormCode(form, code) {
      if (!form || !code) return;
      var input = form.querySelector('[data-discount-code-input]');
      if (input) input.value = code;

      var checkoutForm = getCheckoutForm(form);
      var hiddenInput = checkoutForm ? checkoutForm.querySelector('[data-discount-hidden]') : null;
      if (hiddenInput) hiddenInput.value = code;
    }

    function hydratePreferredDiscountForms(code) {
      code = normalizeDiscountCode(code || getPreferredDiscountCode());
      if (!code) return '';

      document.querySelectorAll('[data-discount-form]').forEach(function (form) {
        setDiscountFormCode(form, code);
      });

      document.querySelectorAll('[data-discount-hidden]').forEach(function (input) {
        input.value = code;
      });

      return code;
    }

    window.themeApplyDiscountCode = function (code) {
      code = normalizeDiscountCode(code);
      if (!code) return '';
      storePreferredDiscountCode(code);
      return hydratePreferredDiscountForms(code);
    };

    function syncDiscountForm(form) {
      if (!form) return '';
      var input = form.querySelector('[data-discount-code-input]');
      var code = normalizeDiscountCode(input ? input.value : '');
      if (input && input.value !== code) input.value = code;

      var checkoutForm = getCheckoutForm(form);
      var hiddenInput = checkoutForm ? checkoutForm.querySelector('[data-discount-hidden]') : null;
      if (hiddenInput) hiddenInput.value = code;
      if (code) storePreferredDiscountCode(code);
      return code;
    }

    function findDiscountFormForCheckout(button) {
      if (!button) return null;
      var checkoutFormId = button.getAttribute('form');
      var checkoutForm = checkoutFormId ? document.getElementById(checkoutFormId) : button.closest('form');
      if (checkoutForm && checkoutForm.id) {
        return document.querySelector('[data-discount-form][data-discount-checkout-form="' + checkoutForm.id + '"]');
      }
      var scope = button.closest('.cart-template__summary, .cart-drawer__footer');
      return scope ? scope.querySelector('[data-discount-form]') : null;
    }

    function applyDiscount(button) {
      var form = button.closest('[data-discount-form]');
      if (!form) return;

      var input = form.querySelector('[data-discount-code-input]');
      var code = syncDiscountForm(form);
      if (!code) {
        setDiscountMessage(form, 'Enter a discount code to apply it at checkout.');
        if (input) input.focus();
        return;
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Opening checkout';
      setDiscountMessage(form, 'Opening secure checkout with ' + code + '. Shopify will confirm eligibility before payment.');
      window.setTimeout(function () {
        window.location.href = getDiscountUrl(code, button.getAttribute('data-discount-redirect') || '/checkout');
      }, 180);
    }

    document.addEventListener('click', function (e) {
      var button = e.target.closest('[data-discount-apply]');
      if (!button) return;
      e.preventDefault();
      applyDiscount(button);
    });

    document.addEventListener('click', function (e) {
      var checkoutButton = e.target.closest('[data-discount-checkout]');
      if (!checkoutButton) return;
      var form = findDiscountFormForCheckout(checkoutButton);
      var code = syncDiscountForm(form);
      if (code) {
        setDiscountMessage(form, code + ' will be checked by Shopify checkout before payment.');
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.keyCode !== 13) return;
      var input = e.target.closest('[data-discount-code-input]');
      if (!input) return;
      var form = input.closest('[data-discount-form]');
      var button = form ? form.querySelector('[data-discount-apply]') : null;
      if (!button) return;
      e.preventDefault();
      applyDiscount(button);
    });

    document.addEventListener('input', function (e) {
      var input = e.target.closest('[data-discount-code-input]');
      if (!input) return;
      syncDiscountForm(input.closest('[data-discount-form]'));
    });

    document.addEventListener('submit', function (e) {
      var checkoutForm = e.target;
      if (!checkoutForm || (checkoutForm.id !== 'CartForm' && checkoutForm.id !== 'CartDrawerForm')) return;
      var form = document.querySelector('[data-discount-form][data-discount-checkout-form="' + checkoutForm.id + '"]');
      syncDiscountForm(form);
    });

    hydratePreferredDiscountForms();
  })();

  // Escape key closes overlays
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      CartDrawer.close();
      MobileMenu.close();
      SearchOverlay.close();
      AccountDrawer.close();
      SupportDrawer.close();
    }
  });

  /* ======================================================================
     STICKY HEADER
     ====================================================================== */
  var StickyHeader = (function () {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var announcementBar = document.querySelector('.announcement-bar');
    var threshold = 0;

    function calculateThreshold() {
      if (announcementBar) {
        threshold = announcementBar.offsetTop + announcementBar.offsetHeight;
      } else {
        threshold = header.offsetHeight;
      }
    }

    function onScroll() {
      if (window.scrollY > threshold) {
        header.classList.add('site-header--scrolled');
      } else {
        header.classList.remove('site-header--scrolled');
      }
    }

    calculateThreshold();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debounce(calculateThreshold, 200));

    // Run once on init
    onScroll();
  })();

  /* ======================================================================
     ANNOUNCEMENT BAR ROTATOR
     ====================================================================== */
  var AnnouncementBar = (function () {
    var slides = document.querySelectorAll('[data-announcement-slide]');
    if (slides.length < 2) return;

    var bar = document.querySelector('.announcement-bar');
    var currentIndex = 0;
    var intervalMs = 5000;
    var timer = null;
    var isPaused = false;

    // Initialize: show first, hide the rest
    slides.forEach(function (slide, i) {
      slide.style.transition = 'opacity 0.5s ease';
      if (i === 0) {
        slide.style.opacity = '1';
        slide.style.position = 'relative';
        slide.style.visibility = 'visible';
      } else {
        slide.style.opacity = '0';
        slide.style.position = 'absolute';
        slide.style.top = '0';
        slide.style.left = '0';
        slide.style.right = '0';
        slide.style.visibility = 'hidden';
      }
    });

    function showSlide(index) {
      slides.forEach(function (slide, i) {
        slide.classList.toggle('announcement-bar__slide--active', i === index);
        if (i === index) {
          slide.style.opacity = '1';
          slide.style.position = 'relative';
          slide.style.visibility = 'visible';
        } else {
          slide.style.opacity = '0';
          slide.style.position = 'absolute';
          slide.style.visibility = 'hidden';
        }
      });
    }

    function next() {
      currentIndex = (currentIndex + 1) % slides.length;
      showSlide(currentIndex);
    }

    function startRotation() {
      if (timer) clearInterval(timer);
      timer = setInterval(function () {
        if (!isPaused) next();
      }, intervalMs);
    }

    if (bar) {
      bar.addEventListener('mouseenter', function () { isPaused = true; });
      bar.addEventListener('mouseleave', function () { isPaused = false; });
    }

    startRotation();
  })();

  /* ======================================================================
     PRODUCT PAGE
     ====================================================================== */
  var ProductPage = (function () {
    var productForms = document.querySelectorAll('[data-product-form]');
    if (!productForms.length) return;

    productForms.forEach(function (form) {
      initProductForm(form);
    });

    function initProductForm(form) {
      var variantSelects = form.querySelectorAll('[data-variant-select]');
      var mainImage = form.closest('.product') ? form.closest('.product').querySelector('[data-main-image]') : document.querySelector('[data-main-image]');
      var thumbnails = form.closest('.product') ? form.closest('.product').querySelectorAll('[data-thumbnail]') : document.querySelectorAll('[data-thumbnail]');
      var productData = null;

      // Attempt to find product JSON
      var productJson = form.querySelector('[type="application/json"][data-product-json]');
      if (!productJson) {
        var section = form.closest('[data-product-json]') || form.closest('.product');
        if (section) {
          productJson = section.querySelector('[type="application/json"][data-product-json]');
        }
      }
      if (!productJson) {
        productJson = document.querySelector('[type="application/json"][data-product-json]');
      }

      if (productJson) {
        try {
          productData = JSON.parse(productJson.textContent);
        } catch (e) {
          console.error('Failed to parse product JSON:', e);
        }
      }

      // Variant selection
      variantSelects.forEach(function (select) {
        select.addEventListener('change', function () {
          var selectedVariant = getSelectedVariant(form, productData);
          if (selectedVariant) {
            updateProductPage(form, selectedVariant, mainImage);
          }
        });
      });

      // Thumbnail gallery
      if (thumbnails.length && mainImage) {
        thumbnails.forEach(function (thumb) {
          thumb.addEventListener('click', function (e) {
            e.preventDefault();
            var src = thumb.getAttribute('data-full-src') || thumb.getAttribute('href') || thumb.querySelector('img').getAttribute('src');
            var alt = thumb.getAttribute('data-alt') || (thumb.querySelector('img') ? thumb.querySelector('img').getAttribute('alt') : '');
            mainImage.setAttribute('src', src);
            if (alt) mainImage.setAttribute('alt', alt);

            // Update active thumbnail
            thumbnails.forEach(function (t) { t.classList.remove('is-active'); });
            thumb.classList.add('is-active');
          });
        });
      }

      // Quantity controls
      var qtyInput = form.querySelector('[data-quantity-input]');
      var minusBtn = form.querySelector('[data-quantity-minus]');
      var plusBtn = form.querySelector('[data-quantity-plus]');

      if (qtyInput && minusBtn && plusBtn) {
        minusBtn.addEventListener('click', function (e) {
          e.preventDefault();
          var val = parseInt(qtyInput.value, 10) - 1;
          if (val < 1) val = 1;
          qtyInput.value = val;
        });

        plusBtn.addEventListener('click', function (e) {
          e.preventDefault();
          var val = parseInt(qtyInput.value, 10) + 1;
          qtyInput.value = val;
        });

        qtyInput.addEventListener('change', function () {
          var val = parseInt(qtyInput.value, 10);
          if (isNaN(val) || val < 1) qtyInput.value = 1;
        });
      }

      // AJAX add-to-cart
      form.addEventListener('submit', function (e) {
        if (window.theme.cartDrawerEnabled) {
          e.preventDefault();

          var submitButton = form.querySelector('[type="submit"]');
          var messageEl = form.querySelector('[data-product-message]');
          function setProductMessage(message, tone) {
            if (!messageEl) return;
            messageEl.textContent = message || '';
            messageEl.hidden = !message;
            messageEl.classList.toggle('pdp__purchase-message--error', tone === 'error');
            messageEl.classList.toggle('pdp__purchase-message--success', tone === 'success');
          }
          if (submitButton) {
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.classList.add('is-loading');
            submitButton.setAttribute('aria-busy', 'true');
          }

          setProductMessage('', '');
          var formData = serializeForm(form);
          var lineItem = {
            id: parseInt(formData.id, 10),
            quantity: Math.max(1, parseInt(formData.quantity, 10) || 1)
          };
          CartDrawer.addItem({ items: [lineItem] })
            .then(function () {
              setProductMessage('Added to cart. You can review your cart or continue shopping.', 'success');
            })
            .catch(function (err) {
              var message = (err && err.description) ? err.description : 'Could not add item to cart.';
              setProductMessage(message, 'error');
            })
            .finally(function () {
              if (submitButton) {
                submitButton.removeAttribute('disabled');
                submitButton.classList.remove('is-loading');
                submitButton.removeAttribute('aria-busy');
              }
            });
        }
      });
    }

    function getSelectedVariant(form, productData) {
      if (!productData || !productData.variants) return null;

      var selects = form.querySelectorAll('[data-variant-select]');
      var selectedOptions = [];
      selects.forEach(function (select) {
        selectedOptions.push(select.value);
      });

      // If there's a single variant select with variant IDs as values, use that
      if (selects.length === 1) {
        var directMatch = productData.variants.find(function (v) {
          return v.id.toString() === selects[0].value;
        });
        if (directMatch) return directMatch;
      }

      // Match based on option values
      var variant = productData.variants.find(function (v) {
        return selectedOptions.every(function (opt, i) {
          return v.options && v.options[i] === opt;
        });
      });

      return variant || null;
    }

    function updateProductPage(form, variant, mainImage) {
      // Update hidden variant ID input
      var variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // Update price display
      var priceContainer = form.closest('.product') ? form.closest('.product').querySelector('.product__price') : document.querySelector('.product__price');
      if (priceContainer) {
        var priceHtml = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml += '<span class="product__price--sale">' + formatMoney(variant.price) + '</span>';
          priceHtml += ' <span class="product__price--compare">' + formatMoney(variant.compare_at_price) + '</span>';
        } else {
          priceHtml += '<span class="product__price--regular">' + formatMoney(variant.price) + '</span>';
        }
        priceContainer.innerHTML = priceHtml;
      }

      // Update add to cart button
      var addBtn = form.querySelector('[type="submit"]');
      if (addBtn) {
        if (variant.available) {
          addBtn.removeAttribute('disabled');
          addBtn.textContent = 'Add to Cart';
        } else {
          addBtn.setAttribute('disabled', 'disabled');
          addBtn.textContent = 'Sold Out';
        }
      }

      // Update URL with variant
      if (variant.id && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      // Update main image if variant has an associated image
      if (variant.featured_image && variant.featured_image.src && mainImage) {
        mainImage.setAttribute('src', variant.featured_image.src);
        if (variant.featured_image.alt) {
          mainImage.setAttribute('alt', variant.featured_image.alt);
        }
      }
    }
  })();

  /* ======================================================================
     PRODUCT ACCORDION
     ====================================================================== */
  (function () {
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-accordion-toggle]');
      if (!toggle) return;

      e.preventDefault();
      var parent = toggle.closest('[data-accordion]');
      if (!parent) return;

      var content = parent.querySelector('[data-accordion-content]');
      if (!content) return;

      var isOpen = parent.classList.contains('is-open');

      if (isOpen) {
        content.style.maxHeight = null;
        parent.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        content.style.maxHeight = content.scrollHeight + 'px';
        parent.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  })();

  /* ======================================================================
     CART PAGE
     ====================================================================== */
  var CartPage = (function () {
    var cartItems = document.querySelectorAll('[data-cart-item-quantity]');
    var cartUpsellButtons = document.querySelectorAll('[data-cart-page-upsell-add]');
    if (!cartItems.length && !cartUpsellButtons.length) return;
    var cartPageMessage = document.querySelector('[data-cart-page-message]');

    function setCartPageMessage(message, tone) {
      if (!cartPageMessage) return;
      cartPageMessage.textContent = message || '';
      cartPageMessage.hidden = !message;
      cartPageMessage.classList.toggle('cart-template__message--error', tone === 'error');
      cartPageMessage.classList.toggle('cart-template__message--success', tone === 'success');
    }

    function getCartErrorMessage(err, fallback) {
      return (err && (err.description || err.message)) ? (err.description || err.message) : fallback;
    }

    function refreshCartPageAfterError(message) {
      fetch(window.theme.routes.cart_url + '.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          updateCartPage(cart);
          setCartPageMessage(message, 'error');
        })
        .catch(function () {
          setCartPageMessage(message, 'error');
        });
    }

    // Debounced quantity update
    var debouncedUpdate = debounce(function (key, quantity) {
      var config = fetchConfig('json');
      config.body = JSON.stringify({ id: key, quantity: quantity });
      setCartPageMessage('', '');

      fetch(window.theme.routes.cart_change_url + '.js', config)
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (data) { throw data; });
          }
          return r.json();
        })
        .then(function (cart) {
          if (cart.item_count === 0) {
            window.location.reload();
          } else {
            updateCartPage(cart);
          }
        })
        .catch(function (err) {
          console.error('Cart page update error:', err);
          refreshCartPageAfterError(getCartErrorMessage(err, 'Could not update your cart. Please try again.'));
        });
    }, 400);

    // Event delegation for cart page
    document.addEventListener('change', function (e) {
      var qtyInput = e.target.closest('[data-cart-item-quantity]');
      if (!qtyInput) return;

      var key = qtyInput.getAttribute('data-key') || qtyInput.getAttribute('data-line');
      var quantity = parseInt(qtyInput.value, 10);
      if (isNaN(quantity) || quantity < 0) quantity = 0;

      debouncedUpdate(key, quantity);
    });

    document.addEventListener('click', function (e) {
      var minusBtn = e.target.closest('[data-quantity-minus]');
      if (minusBtn) {
        var minusSelector = minusBtn.closest('.quantity-selector');
        var minusInput = minusSelector ? minusSelector.querySelector('[data-cart-item-quantity]') : null;
        if (minusInput) {
          e.preventDefault();
          var minusQty = parseInt(minusInput.value, 10) - 1;
          if (isNaN(minusQty) || minusQty < 0) minusQty = 0;
          minusInput.value = minusQty;
          debouncedUpdate(minusInput.getAttribute('data-key') || minusInput.getAttribute('data-line'), minusQty);
          return;
        }
      }

      var plusBtn = e.target.closest('[data-quantity-plus]');
      if (plusBtn) {
        var plusSelector = plusBtn.closest('.quantity-selector');
        var plusInput = plusSelector ? plusSelector.querySelector('[data-cart-item-quantity]') : null;
        if (plusInput) {
          e.preventDefault();
          var plusQty = parseInt(plusInput.value, 10) + 1;
          if (isNaN(plusQty) || plusQty < 1) plusQty = 1;
          plusInput.value = plusQty;
          debouncedUpdate(plusInput.getAttribute('data-key') || plusInput.getAttribute('data-line'), plusQty);
          return;
        }
      }

      var upsellBtn = e.target.closest('[data-cart-page-upsell-add]');
      if (upsellBtn) {
        e.preventDefault();
        var variantId = parseInt(upsellBtn.getAttribute('data-variant-id'), 10);
        if (!variantId) return;

        if (window.themeApplyDiscountCode) {
          window.themeApplyDiscountCode('BUNDLE25');
        }
        upsellBtn.disabled = true;
        upsellBtn.classList.add('is-loading');
        upsellBtn.textContent = 'Adding';

        var addConfig = fetchConfig('json');
        addConfig.body = JSON.stringify({ items: [{ id: variantId, quantity: 1 }] });
        setCartPageMessage('', '');

        fetch(window.theme.routes.cart_add_url + '.js', addConfig)
          .then(function (r) {
            if (!r.ok) {
              return r.json().then(function (data) { throw data; });
            }
            return r.json();
          })
          .then(function () {
            window.location.reload();
          })
          .catch(function (err) {
            console.error('Cart upsell add error:', err);
            upsellBtn.disabled = false;
            upsellBtn.classList.remove('is-loading');
            upsellBtn.textContent = 'Add';
            setCartPageMessage(getCartErrorMessage(err, 'Could not add this item. Please check availability.'), 'error');
          });
        return;
      }

      var removeBtn = e.target.closest('[data-cart-item-remove]');
      if (!removeBtn) return;

      e.preventDefault();
      var key = removeBtn.getAttribute('data-key') || removeBtn.getAttribute('data-line');

      var config = fetchConfig('json');
      config.body = JSON.stringify({ id: key, quantity: 0 });
      setCartPageMessage('', '');

      // Visually fade out the item row
      var itemRow = removeBtn.closest('.cart-template__item') || removeBtn.closest('tr') || removeBtn.closest('[data-cart-item]');
      if (itemRow) {
        itemRow.style.opacity = '0.5';
        itemRow.style.pointerEvents = 'none';
      }

      fetch(window.theme.routes.cart_change_url + '.js', config)
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (data) { throw data; });
          }
          return r.json();
        })
        .then(function (cart) {
          if (cart.item_count === 0) {
            window.location.reload();
          } else {
            updateCartPage(cart);
            if (itemRow) itemRow.remove();
            setCartPageMessage('Item removed.', 'success');
          }
        })
        .catch(function (err) {
          console.error('Cart remove error:', err);
          if (itemRow) {
            itemRow.style.opacity = '';
            itemRow.style.pointerEvents = '';
          }
          refreshCartPageAfterError(getCartErrorMessage(err, 'Could not remove this item. Please try again.'));
        });
    });

    function updateCartPage(cart) {
      var currentKeys = (cart.items || []).map(function (item) { return item.key; });
      document.querySelectorAll('[data-cart-item]').forEach(function (row) {
        var rowKey = row.getAttribute('data-cart-item-key');
        if (rowKey && currentKeys.indexOf(rowKey) === -1) {
          row.remove();
        }
      });

      // Update subtotal
      var subtotalEls = document.querySelectorAll('[data-cart-subtotal]');
      subtotalEls.forEach(function (el) {
        el.textContent = formatMoney(cart.total_price);
      });

      // Update item prices and quantities
      cart.items.forEach(function (item) {
        var priceEls = document.querySelectorAll('[data-cart-item-price][data-key="' + item.key + '"]');
        priceEls.forEach(function (el) {
          el.textContent = formatMoney(item.final_line_price);
        });

        var qtyInputs = document.querySelectorAll('[data-cart-item-quantity][data-key="' + item.key + '"]');
        qtyInputs.forEach(function (el) {
          el.value = item.quantity;
        });
      });

      // Update header cart count
      var badges = document.querySelectorAll('.cart-count');
      badges.forEach(function (badge) {
        badge.textContent = cart.item_count;
        if (cart.item_count > 0) {
          badge.classList.add('cart-count--has-items');
        } else {
          badge.classList.remove('cart-count--has-items');
        }
      });

      var pageProgress = document.querySelector('[data-cart-page-progress]');
      if (pageProgress) {
        var threshold = parseInt(pageProgress.getAttribute('data-threshold'), 10) || 15000;
        var remaining = Math.max(threshold - cart.total_price, 0);
        var percent = Math.min(Math.round((cart.total_price / threshold) * 100), 100);
        var text = pageProgress.querySelector('.cart-template__progress-text');
        var bar = pageProgress.querySelector('.cart-template__progress-track span');
        if (text) {
          text.textContent = remaining > 0
            ? 'You are ' + formatMoney(remaining) + ' away from free shipping.'
            : 'Free shipping unlocked.';
        }
        if (bar) bar.style.width = percent + '%';
      }
    }
  })();

  /* ======================================================================
     HORIZONTAL SCROLL (DRAG TO SCROLL)
     ====================================================================== */
  (function () {
    var tracks = document.querySelectorAll('.scrolling-collections__track');
    if (!tracks.length) return;

    tracks.forEach(function (track) {
      var isDown = false;
      var startX;
      var scrollLeft;
      var hasMoved = false;

      track.style.cursor = 'grab';
      track.style.userSelect = 'none';

      track.addEventListener('mousedown', function (e) {
        isDown = true;
        hasMoved = false;
        track.style.cursor = 'grabbing';
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
      });

      track.addEventListener('mouseleave', function () {
        if (isDown) {
          isDown = false;
          track.style.cursor = 'grab';
        }
      });

      track.addEventListener('mouseup', function () {
        isDown = false;
        track.style.cursor = 'grab';
      });

      track.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        hasMoved = true;
        var x = e.pageX - track.offsetLeft;
        var walk = (x - startX) * 1.5;
        track.scrollLeft = scrollLeft - walk;
      });

      // Prevent click on child links when dragging
      track.addEventListener('click', function (e) {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Touch support for smooth scrolling feel
      track.style.scrollBehavior = 'auto';
      track.style.webkitOverflowScrolling = 'touch';
    });
  })();

  /* ======================================================================
     UGC VIDEO CAROUSEL (AUTO-SCROLLING REELS)
     ====================================================================== */
  (function () {
    var carousels = document.querySelectorAll('[data-ugc-carousel]');
    if (!carousels.length) return;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    carousels.forEach(function (carousel) {
      var track = carousel.querySelector('[data-ugc-carousel-track]');
      var prev = carousel.querySelector('[data-ugc-carousel-prev]');
      var next = carousel.querySelector('[data-ugc-carousel-next]');
      var interval = parseInt(carousel.getAttribute('data-ugc-carousel-interval'), 10) || 3600;
      var index = 0;
      var timer = null;
      var paused = false;
      var visible = !('IntersectionObserver' in window);
      var resumeTimer = null;
      var scrollTimer = null;

      if (!track) return;

      var originalSlides = Array.prototype.slice.call(track.querySelectorAll('[data-ugc-carousel-slide]'));
      if (originalSlides.length < 2) return;

      originalSlides.forEach(function (slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-ugc-carousel-clone', '');
        clone.querySelectorAll('a, button, input, select, textarea, video, [tabindex]').forEach(function (item) {
          item.setAttribute('tabindex', '-1');
          if (item.tagName === 'VIDEO') item.controls = false;
        });
        track.appendChild(clone);
      });

      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-ugc-carousel-slide]'));
      var originalCount = originalSlides.length;

      if (!track || slides.length < 2) return;

      function targetLeft(slide) {
        return slide.offsetLeft - track.offsetLeft;
      }

      function setActive(nextIndex, behavior) {
        index = nextIndex;
        if (index < 0) index = originalCount - 1;

        track.scrollTo({
          left: targetLeft(slides[index]),
          behavior: behavior || 'smooth'
        });

        if (index >= originalCount) {
          window.setTimeout(function () {
            track.scrollTo({
              left: targetLeft(slides[0]),
              behavior: 'auto'
            });
            index = 0;
          }, 700);
        }
      }

      function syncIndex() {
        var left = track.scrollLeft;
        var nearestIndex = 0;
        var nearestDistance = Infinity;

        slides.forEach(function (slide, i) {
          var distance = Math.abs(targetLeft(slide) - left);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = i;
          }
        });

        index = nearestIndex % originalCount;
      }

      function tick() {
        if (paused || !visible || document.hidden || reduceMotion) return;
        setActive(index + 1, 'smooth');
      }

      function start() {
        if (timer || reduceMotion) return;
        timer = setInterval(tick, interval);
      }

      function stop() {
        if (!timer) return;
        clearInterval(timer);
        timer = null;
      }

      function pause() {
        paused = true;
        clearTimeout(resumeTimer);
      }

      function resumeSoon() {
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(function () {
          paused = false;
        }, 1800);
      }

      if (prev) {
        prev.addEventListener('click', function () {
          pause();
          setActive(index - 1, 'smooth');
          resumeSoon();
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          pause();
          setActive(index + 1, 'smooth');
          resumeSoon();
        });
      }

      track.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(syncIndex, 120);
      });

      carousel.addEventListener('mouseenter', pause);
      carousel.addEventListener('mouseleave', resumeSoon);
      carousel.addEventListener('focusin', pause);
      carousel.addEventListener('focusout', resumeSoon);
      carousel.addEventListener('touchstart', pause, { passive: true });
      carousel.addEventListener('touchend', resumeSoon, { passive: true });
      carousel.addEventListener('pointerdown', pause);
      carousel.addEventListener('pointerup', resumeSoon);

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      });

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            visible = entry.isIntersecting;
          });
        }, { threshold: 0.2 });
        observer.observe(carousel);
      }

      start();
    });
  })();

  /* ======================================================================
     PDP SOURCE-STYLE ACCORDION FALLBACK
     ====================================================================== */
  function initPdpSourceAccordionFallback() {
    var roots = Array.prototype.slice.call(document.querySelectorAll('.product-template--cora'));
    if (!roots.length) return;

    function blockHtml(panel, title, keepHeading) {
      if (!panel) return '';
      var blocks = Array.prototype.slice.call(panel.querySelectorAll('.pdp__metafield-block'));
      var match = blocks.find(function (block) {
        var heading = block.querySelector('h3');
        return heading && heading.textContent.trim().toLowerCase() === title.toLowerCase();
      });
      if (!match) return '';

      var clone = match.cloneNode(true);
      if (!keepHeading) {
        var cloneHeading = clone.querySelector('h3');
        if (cloneHeading) cloneHeading.remove();
      }
      return clone.innerHTML;
    }

    function createRow(label, html) {
      var details = document.createElement('details');
      details.className = 'pdp__source-detail';

      var summary = document.createElement('summary');
      summary.textContent = label;

      var body = document.createElement('div');
      body.className = 'pdp__source-body rte';
      body.innerHTML = html || '<p>Product details are being updated.</p>';

      details.appendChild(summary);
      details.appendChild(body);
      return details;
    }

    function fillAccordion(host, contents, wrap) {
      var target = host;
      host.innerHTML = '';

      if (wrap) {
        target = document.createElement('div');
        target.className = 'pdp__source-accordion';
        target.setAttribute('aria-label', 'Product details');
        host.appendChild(target);
      } else {
        host.classList.add('pdp__source-accordion');
        host.setAttribute('aria-label', 'Product details and help');
      }

      target.appendChild(createRow('Product Details', contents.productDetails));
      target.appendChild(createRow('Benefits + Claims', contents.benefitsClaims));
      target.appendChild(createRow('How To Use', contents.howToUse));
      target.appendChild(createRow('Pair It With', contents.pairItWith));
    }

    function sourceDetailHtml(root, label) {
      var details = Array.prototype.slice.call(root.querySelectorAll('.pdp__source-detail'));
      var match = details.find(function (item) {
        var summary = item.querySelector('summary');
        return summary && summary.textContent.trim().toLowerCase() === label.toLowerCase();
      });
      var body = match ? match.querySelector('.pdp__source-body') : null;
      return body ? body.innerHTML : '';
    }

    function createFallbackStars() {
      var stars = document.createElement('span');
      stars.className = 'pdp__rating-stars';
      stars.setAttribute('aria-hidden', 'true');

      for (var i = 0; i < 5; i += 1) {
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', 'pdp__rating-star is-filled');
        svg.setAttribute('viewBox', '0 0 20 20');
        svg.setAttribute('focusable', 'false');
        var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M10 1.6l2.5 5.1 5.6.8-4 4 1 5.6-5.1-2.7-5 2.7 1-5.6-4.1-4 5.6-.8L10 1.6z');
        svg.appendChild(path);
        stars.appendChild(svg);
      }

      return stars;
    }

    function hydrateSourceStory(root) {
      var container = root.querySelector('.pdp__info-container');
      if (!container || container.querySelector('.pdp__source-tax-note')) return;

      var titleRow = container.querySelector('.pdp__title-row');
      var priceRow = container.querySelector('.pdp__price-row');
      if (!titleRow || !priceRow) return;

      // The Liquid template owns verified review data. Never synthesize a
      // rating or count in this fallback.

      if (!container.querySelector('.pdp__source-tax-note')) {
        var taxNote = document.createElement('p');
        taxNote.className = 'pdp__source-tax-note';
        taxNote.textContent = 'Tax included. Shipping calculated at checkout.';
        priceRow.insertAdjacentElement('afterend', taxNote);

        var summary = document.createElement('div');
        summary.className = 'pdp__source-summary rte';

        var description = root.querySelector('.pdp__description-copy');
        if (description && description.textContent.trim()) {
          var lede = document.createElement('div');
          lede.className = 'pdp__source-lede';
          lede.textContent = description.textContent.trim();
          summary.appendChild(lede);
        }

        var benefitsHtml = sourceDetailHtml(root, 'Benefits + Claims') || sourceDetailHtml(root, 'Product Details');
        if (benefitsHtml) {
          var benefits = document.createElement('div');
          benefits.className = 'pdp__source-benefits';
          benefits.innerHTML = benefitsHtml;
          summary.appendChild(benefits);
        }

        taxNote.insertAdjacentElement('afterend', summary);

        var targets = document.createElement('div');
        targets.className = 'pdp__source-targets';
        targets.setAttribute('aria-label', 'Targets');
        targets.innerHTML = '<p>Targets</p><div><span>Puffiness + Fluid Retention</span><span>Dullness + Texture</span><span>Uneven Tone + Pigmentation</span><span>Breakouts + Congestion</span></div>';
        summary.insertAdjacentElement('afterend', targets);
      }
    }

    function shouldUseSourceAccordion(root) {
      var explicitFlag = root.getAttribute('data-source-accordion-product');
      if (explicitFlag === 'true') return true;
      if (explicitFlag === 'false') return false;

      return !!root.querySelector('.pdp__breadcrumb a[href*="/collections/afterglow"]');
    }

    roots.forEach(function (root) {
      var useSourceAccordion = shouldUseSourceAccordion(root);
      root.classList.toggle('pdp--source-layout', useSourceAccordion);

      if (!useSourceAccordion) return;
      hydrateSourceStory(root);
      var sourceAccordions = Array.prototype.slice.call(root.querySelectorAll('.pdp__source-accordion'));
      if (sourceAccordions.length) {
        // Desktop and mobile render the same disclosures in different positions.
        // Keep every visible copy aligned with the canonical, concise Liquid
        // content so stale metafield dumps cannot reappear at another breakpoint.
        var canonicalAccordionHtml = sourceAccordions[0].innerHTML;
        sourceAccordions.slice(1).forEach(function (accordion) {
          accordion.innerHTML = canonicalAccordionHtml;
        });
        return;
      }

      var desktop = root.querySelector('.pdp__accordion-main');
      if (!desktop || !desktop.querySelector('.pdp__tabs')) return;

      var detailsPanel = desktop.querySelector('[data-panel="details"]');
      var fitPanel = desktop.querySelector('[data-panel="fitfabric"]');
      var mobile = root.querySelector('.pdp__mobile-faqs');
      var productDetailsParts = [];

      if (detailsPanel) productDetailsParts.push(detailsPanel.innerHTML);

      [
        ['size & fit', false],
        ['fabrication', true],
        ['materials & care', true],
        ['compatibility', true],
        ['warranty & support', true]
      ].forEach(function (entry) {
        var html = blockHtml(fitPanel, entry[0], entry[1]);
        if (html) productDetailsParts.push(html);
      });

      var contents = {
        productDetails: productDetailsParts.join(''),
        benefitsClaims: blockHtml(fitPanel, 'model details', false) || blockHtml(fitPanel, 'compatibility', false),
        howToUse: blockHtml(fitPanel, 'care', false),
        pairItWith: '<p>Pair it with the tools in the Bundle and Save 25% edit below.</p><p><a href="#bundle-and-save">Shop the bundle edit</a></p>'
      };

      fillAccordion(desktop, contents, true);
      if (mobile) fillAccordion(mobile, contents, false);
    });
  }

  function initPdpSourceAccordionAnimations() {
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var detailsItems = Array.prototype.slice.call(document.querySelectorAll('.product-template--cora .pdp__source-detail'));

    detailsItems.forEach(function (details) {
      if (details.dataset.smoothAccordion === 'true') return;
      details.dataset.smoothAccordion = 'true';

      var summary = details.querySelector('summary');
      var body = details.querySelector('.pdp__source-body');
      if (!summary || !body) return;

      summary.addEventListener('click', function (event) {
        if (reduceMotion || !body.animate) return;
        event.preventDefault();
        if (details.classList.contains('is-animating')) return;

        var opening = !details.open;
        var targetHeight;
        details.classList.add('is-animating');

        if (opening) {
          details.open = true;
          targetHeight = body.scrollHeight;
        } else {
          targetHeight = body.getBoundingClientRect().height;
        }

        var animation = body.animate(
          opening
            ? [
                { height: '0px', opacity: 0 },
                { height: targetHeight + 'px', opacity: 1 }
              ]
            : [
                { height: targetHeight + 'px', opacity: 1 },
                { height: '0px', opacity: 0 }
              ],
          {
            duration: 240,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
          }
        );

        animation.onfinish = function () {
          if (!opening) details.open = false;
          details.classList.remove('is-animating');
          body.style.height = '';
          body.style.opacity = '';
        };

        animation.oncancel = function () {
          details.classList.remove('is-animating');
          body.style.height = '';
          body.style.opacity = '';
        };
      });
    });
  }

  /* ======================================================================
     ANIMATION ON SCROLL (LAZY ANIMATION)
     ====================================================================== */
  (function () {
    var animateElements = document.querySelectorAll('.animate-in');
    if (!animateElements.length) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px'
      });

      animateElements.forEach(function (el) {
        observer.observe(el);
      });
    } else {
      // Fallback: just show everything
      animateElements.forEach(function (el) {
        el.classList.add('is-visible');
      });
    }
  })();

  /* ======================================================================
     INITIALIZATION
     ====================================================================== */
  function init() {
    document.querySelectorAll('a[href*="/pages/preorder-restocks"]').forEach(function (link) {
      link.remove();
    });

    initPdpSourceAccordionFallback();
    initPdpSourceAccordionAnimations();
    initializeResultsComparisons(document);

    // On page load, refresh the cart count badge from the API
    fetch(window.theme.routes.cart_url + '.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var badges = document.querySelectorAll('.cart-count');
        badges.forEach(function (badge) {
          badge.textContent = cart.item_count;
          if (cart.item_count > 0) {
            badge.classList.add('cart-count--has-items');
          } else {
            badge.classList.remove('cart-count--has-items');
          }
        });
      })
      .catch(function () {});
  }

  // Run when DOM is ready (the script uses defer, so DOM is already parsed)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('a[href*="/pages/preorder-restocks"]').forEach(function (link) {
      link.remove();
    });
    initializeResultsComparisons(event.target);
  });

  /* ======================================================================
     EXPOSE PUBLIC API
     ====================================================================== */
  window.theme = window.theme || {};
  window.theme.cart = CartDrawer;
  window.theme.formatMoney = formatMoney;
  window.theme.debounce = debounce;
  window.theme.fetchConfig = fetchConfig;
  window.theme.serializeForm = serializeForm;

})();
